/**
 * Documents-tab notice for the ALPR preservation demand and the Article 39.14 request.
 *
 * Hidden when this case does not use those letters. Two independent parts when it does: a warning
 * naming any required merge field still blank, and the ALPR search radius.
 *
 * The radius is here rather than on the case form because it is a tactical choice made when the
 * request is being prepared, not a fact recorded at intake. It is required — Lloyd, Sep 4 2026:
 * *"this needs to be a required input field, not something that can silently ship blank"* — and a
 * suggestion is prefilled so making the choice is one click rather than a decision from nothing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Paper, TextField, Typography } from '@mui/material';
import AlprDocumentsApi, {
  AlprDocumentsReadinessDto,
  FieldCoverageReport,
} from '../../api/AlprDocumentsApi';

interface AlprReadinessBannerProps {
  Ticket_guid: string;
}

function missingLabels(report: FieldCoverageReport): string[] {
  return report.missingRequired.map((field) => field.label);
}

const AlprReadinessBanner: React.FC<AlprReadinessBannerProps> = ({ Ticket_guid }) => {
  const [readiness, setReadiness] = useState<AlprDocumentsReadinessDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [radius, setRadius] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await AlprDocumentsApi.readiness(Ticket_guid);
      setReadiness(next);
      /*
       * The suggestion fills the box only when the case holds nothing, and is never itself a value:
       * the request is not ready until somebody saves one. Prefilling costs nothing and saves the
       * common case, but a number nobody looked at must not reach a document served on the State.
       */
      setRadius(
        next.searchRadiusMiles !== null
          ? String(next.searchRadiusMiles)
          : String(next.suggestedSearchRadiusMiles),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check ALPR document readiness');
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const saveRadius = async () => {
    setSaving(true);
    setError(null);
    try {
      await AlprDocumentsApi.setSearchRadius(Ticket_guid, radius.trim() ? Number(radius) : null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set the search radius');
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  if (!readiness || !readiness.applies) {
    return null;
  }

  const preservationMissing = missingLabels(readiness.preservationDemand);
  const articleMissing = missingLabels(readiness.article3914Request);
  const recorded = readiness.searchRadiusMiles !== null;

  return (
    <>
      {(preservationMissing.length > 0 || articleMissing.length > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>ALPR letters are missing fields on this case</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The preservation demand and the Article 39.14 request print a blank for anything not
            recorded here. Fill these before those letters go out.
          </Typography>
          {preservationMissing.length > 0 && (
            <Typography variant="body2">
              Preservation demand: {preservationMissing.join(', ')}
            </Typography>
          )}
          {articleMissing.length > 0 && (
            <Typography variant="body2">
              Article 39.14 request: {articleMissing.join(', ')}
            </Typography>
          )}
        </Alert>
      )}

      {/*
        Item 4 asks for detections "within ___ miles of the location of the alleged offense". Shown
        whether or not anything else is missing, so there is somewhere to set it on a case that is
        otherwise ready.
      */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          ALPR search radius
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            label="Miles"
            type="number"
            size="small"
            sx={{ width: 140 }}
            value={radius}
            disabled={saving}
            onChange={(e) => setRadius(e.target.value)}
          />
          <Button size="small" variant="contained" disabled={saving} onClick={saveRadius}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {recorded
              ? `Recorded as ${readiness.searchRadiusMiles} miles.`
              : `Required before this request can go out. ${readiness.suggestedSearchRadiusMiles} miles is a suggestion, not a value: change it if you want, then save.`}
          </Typography>
        </Box>
      </Paper>
    </>
  );
};

export default AlprReadinessBanner;
